import {computed, nextTick, onBeforeUnmount, onMounted, shallowRef, toValue, watch, type MaybeRefOrGetter} from 'vue';
import {useRoute} from '#imports';
import {useDisplayMobile} from '@malt/utils';
import {useNavbarMode} from './useNavbarMode';

const TRANSPARENT_NAVBAR_ATTRIBUTE = 'data-navbar-transparent';

/**
 * Overlays the bar until the user scrolls on pages whose content sets `data-navbar-transparent` on `<html>`
 * (through `useHead`).
 * On desktop the bar stays transparent until NavbarTop (`--navbar-top-height`) has scrolled away.
 * An open mobile menu restores the solid bar, because the drawer is an opaque panel.
 */
export function useNavbarTransparency(isMenuOpen: MaybeRefOrGetter<boolean>) {
    const route = useRoute();
    const {hasMultipleModes} = useNavbarMode();
    const {isDisplayMobile} = useDisplayMobile('Sm');
    const isScrolled = shallowRef(false);
    const isTransparentNavbarRequested = shallowRef(false);

    const isOverlay = computed(() => isTransparentNavbarRequested.value);

    const isTransparent = computed(() => isOverlay.value && !isScrolled.value && !toValue(isMenuOpen));

    function readTransparentNavbarRequest() {
        isTransparentNavbarRequested.value = document.documentElement.hasAttribute(TRANSPARENT_NAVBAR_ATTRIBUTE);
    }

    function readScroll() {
        if (!import.meta.client) {
            return;
        }
        // NavbarTop is in normal flow only on desktop, and only when several modes are shown.
        const navbarTopHeight = !isDisplayMobile.value && hasMultipleModes.value ? readNavbarTopHeight() : 0;
        isScrolled.value = window.scrollY > 0 && window.scrollY >= navbarTopHeight;
    }

    const transparentNavbarRequestObserver = shallowRef<MutationObserver>();

    onMounted(() => {
        readTransparentNavbarRequest();
        readScroll();
        transparentNavbarRequestObserver.value = new MutationObserver(readTransparentNavbarRequest);
        transparentNavbarRequestObserver.value.observe(document.documentElement, {attributes: true, attributeFilter: [TRANSPARENT_NAVBAR_ATTRIBUTE]});
        window.addEventListener('scroll', readScroll, {passive: true});
        window.addEventListener('resize', readScroll, {passive: true});
    });

    onBeforeUnmount(() => {
        transparentNavbarRequestObserver.value?.disconnect();
        window.removeEventListener('scroll', readScroll);
        window.removeEventListener('resize', readScroll);
    });

    watch(isDisplayMobile, readScroll);

    watch(
        () => route.path,
        () => {
            if (!import.meta.client) {
                return;
            }
            nextTick(readScroll);
        },
    );

    return {isOverlay, isTransparent};
}

function readNavbarTopHeight(): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--navbar-top-height');
    const height = Number.parseFloat(raw);
    return Number.isFinite(height) ? height : 0;
}
